import { datadogLogs } from '@datadog/browser-logs';
import { useCallback } from 'react';

import useSnackbarProvider from 'src/hooks/useSnackbarProvider';
import { useAppDispatch } from 'src/store';
import { IProof } from 'src/ts/interfaces';
import { requestDocumentsThunk } from '../services';

export const useRequestDocuments = () => {
  const dispatch = useAppDispatch();
  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();

  const onRequestDocuments = useCallback(
    async (entryId?: string, message?: string, proofs?: IProof[]) => {
      if (!entryId || !message) {
        showSnackbar(VariantType.error, 'Failed to request documents', SnackTypes.none);

        return;
      }

      try {
        await dispatch(requestDocumentsThunk({ id: entryId, message, proofs })).unwrap();

        showSnackbar(VariantType.success, 'Your email request has been sent', SnackTypes.none);
      } catch (error: any) {
        // ITL-2816: requestDocumentsThunk rejects with the backend body via
        // rejectWithValue — shape: { error: { code }, pending_proof_ids }.
        const code = error?.error?.code;

        // ITL-2816 (C3): mirror useSendReport's Datadog error log so a guard-blocked
        // request_documents is observable (the 422's pending_proof_ids would
        // otherwise be silently discarded). Conditional fields per C9.
        datadogLogs.logger.error('request_documents failed', {
          event: 'entries.request_documents_failed',
          entry_id: entryId,
          proof_ids: proofs?.map((p) => p.id),
          ...(code ? { error_code: code } : {}),
          ...(error?.pending_proof_ids ? { pending_proof_ids: error.pending_proof_ids } : {}),
          error: error?.error?.message ?? (error instanceof Error ? error.message : String(error))
        });

        showSnackbar(
          VariantType.error,
          code === 'pending_proofs_unresolved'
            ? 'Some documents are still pending review. Resolve all document rulings before requesting documents.'
            : 'An error has occurred, please refresh and try again',
          SnackTypes.none
        );
      }
    },
    [SnackTypes.none, VariantType.error, VariantType.success, dispatch, showSnackbar]
  );

  return {
    onRequestDocuments
  };
};
